import { ReactElement } from 'react';
import styled, { css } from 'styled-components';
import { ifProp, palette, theme } from 'styled-tools';

import { ChevronLeft as chevronLeft, CloseHeavy as closeHeavy } from '@calm-web/icons';

import { maxWidth, minWidth } from '../../utils/mediaQueries';
import { units } from '../../utils/spacing';
import { Optional } from '../../utils/types';
import { FontWeights } from '../../utils/typography';
import { ButtonIcon } from '../Button/styles';
import IconButton from '../IconButton';
import { IconButtonProps } from '../IconButton/types';
import Text from '../Text';
import { TextElements } from '../Text/types';
import { CalmTheme } from '../ThemeProvider';
import { ModalWidth } from './types';

export const ModalAndOverlayContainer = styled.div<{
	$UNSAFE_positionAbsolute?: boolean;
}>`
	position: ${p => (p.$UNSAFE_positionAbsolute ? 'absolute' : 'fixed')};
	left: 0;
	top: 0;
	width: 100%;
	height: 100vh;
	min-height: 100%;
	z-index: ${theme('modal.zIndex')};
`;

export const Overlay = styled.div`
	position: absolute;
	width: 100%;
	height: 100%;
	content: '${palette('modalOverlayBg')}';
	background-color: ${theme('modal.overlayBg')};
	transition: all 0.3s ease;
	backdrop-filter: blur(10px);
	z-index: 0;
`;

export const OverlayClickHandler = styled.div`
	position: absolute;
	width: 100%;
	height: 100%;
	content: 'transparent';
	background-color: transparent;
	z-index: 0;
`;

export const ModalContainer = styled.div<{
	$width: ModalWidth;
}>`
	position: absolute;
	left: 50%;
	top: 50%;
	transform: translateX(-50%) translateY(-50%);
	width: ${p => {
		switch (p.$width) {
			case ModalWidth.Normal:
				return units(68);
			case ModalWidth.Extra:
				return units(84);
			case ModalWidth.Ultra:
				return units(100);
			case ModalWidth.Super:
				return units(157);
			case ModalWidth.Full:
				return '100%';
		}
	}};
	max-width: 100%;

	${maxWidth('mobileLarge')} {
		width: 100%;
		max-height: 100%;
		overflow-y: scroll;
	}
`;

export const ModalBody = styled.div<{
	$backgroundColor: string;
	$isGradientBorderColor?: boolean;
	$borderColor?: string;
}>`
	background-color: ${p => palette(p.$backgroundColor)};
	${ifProp(
		'$isGradientBorderColor',
		({ $borderColor = 'modalBorder' }) =>
			css`
				background: linear-gradient(white, white) padding-box, ${palette($borderColor)} border-box;
				border: 4px solid transparent;
			`,
		ifProp(
			'$borderColor',
			({ $borderColor = 'modalBorder' }) => css`
				border: 1px solid ${palette($borderColor)};
			`,
		),
	)};
	border-radius: ${theme('modal.borderRadius')};
	box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.25);
	opacity: 1;
	transition: all 0.5s ease-out;
	box-sizing: border-box;
	position: relative;
	display: flex;
	flex-direction: column;
	max-height: 100vh;
`;

export const ChildContainer = styled.div<{
	$hasTopButtons: boolean;
	$hasTitle: boolean;
	$noPadding: boolean;
	$hasOverflowY: boolean;
}>`
	${p =>
		!p.$noPadding &&
		css`
			padding: 3rem 1.5rem;
			${p.$hasTitle ? 'padding-top: 1rem;' : p.$hasTopButtons ? 'margin-top: 1.5rem;' : ''}
			${minWidth('mobileLarge')} {
				padding: ${theme('modal.padding')};
				${p.$hasTitle ? 'padding-top: 1rem;' : p.$hasTopButtons ? 'margin-top: 2rem;' : ''}
			}
			${p.$hasOverflowY ? 'overflow-y: auto;' : 'none'}
		`}
	flex: 0 1 auto;
`;

export const CloseIconContainer = styled(IconButton)`
	position: absolute;
	top: ${theme('modal.closeButtonPadding')};
	right: ${theme('modal.closeButtonPadding')};
	height: 36px;
	width: 36px;
	overflow: hidden;

	${ButtonIcon} {
		display: flex;
		height: auto;
		width: 16px;
		svg {
			height: 16px;
			width: 16px;
		}
	}
`;

export const CloseIcon = (props: Omit<Optional<IconButtonProps, 'aria-label'>, 'Icon'>): ReactElement => {
	return <CloseIconContainer aria-label="close" {...props} Icon={closeHeavy} />;
};

export const BackIconContainer = styled(IconButton)`
	position: absolute;
	top: 0;
	left: 0;
	cursor: pointer;
	padding: 1rem;
	height: 4rem;
	width: 4rem;
	overflow: hidden;

	${ButtonIcon} {
		height: 2rem;
		width: 2rem;
		svg {
			height: 2rem;
			width: 2rem;
		}
	}
`;

export const BackIcon = (props: Omit<IconButtonProps, 'Icon'>): ReactElement => {
	return <BackIconContainer {...props} Icon={chevronLeft} />;
};

export const TitleContainer = styled.div<{
	$titleIsCentered: boolean;
	$hasLeftButton?: boolean;
	$hasRightButton?: boolean;
}>`
	width: 100%;
	border-bottom: ${theme('modal.titleBottomBorder')} solid ${palette('modalTitleBorder')};
	padding: ${theme('modal.titleTopPadding')} ${theme('modal.padding')} 1rem;
	${p => (p.$hasLeftButton || (p.$titleIsCentered && p.$hasRightButton) ? 'padding-left: 3.5rem;' : '')}
	${p => (p.$hasRightButton || (p.$titleIsCentered && p.$hasLeftButton) ? 'padding-right: 3.5rem;' : '')}
`;

export const Title = styled(Text).attrs(props => {
	return {
		el: TextElements.H2,
		weight: FontWeights.Medium,
		noMargin: true,
		size: (props.theme as typeof CalmTheme).modal.titleSize,
		align: (props.theme as typeof CalmTheme).modal.titleAlign,
	};
})``;

export const ModalFooter = styled.div<{ topBorder: boolean }>`
	display: flex;
	width: 100%;
	justify-content: center;
	align-items: center;
	padding: ${units(2, 0)};
	${(props): string => (props.topBorder ? `border-top: 1px solid ${palette('gray1')(props)}` : '')};
`;
